import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import {
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_EXPERIENCE,
  OFFER_TYPE_EXPERIENCE_TRANSFER,
  PRODUCT_TYPE_CAR_HIRE,
  PRODUCT_TYPE_TOUR_ULTRA_LUX,
  PRODUCT_TYPE_TOUR_PREMIUM,
  PRODUCT_TYPE_TOUR_DELUXE,
  PRODUCT_TYPE_TOUR_SIGNATURE_SERIES,
  PRODUCT_TYPE_TOUR_CUSTOM,
} from 'constants/offer'
import { arrayToMap, arrayToObject, firstAndLast, min, sum } from 'lib/array/arrayUtils'

import { match } from 'ts-pattern'
import { isTourV2Offer, isCarHireOffer, isLpcHotelOffer, isBedbankOffer, isExperienceOffer, isCruiseOffer, isLEHotel } from 'lib/offer/offerTypes'
import type { AgentHubGetOffersCommissionBody } from '../agentHubCommissionTypes'
import { DIRECT_TOUR } from 'constants/tours'
import type { actions } from '@luxuryescapes/contract-svc-agent'
import { mapBedbankPackageToAgentHubPayload, mapFlashPackageToAgentHubPayload, mapLPCPackageToAgentHubPayload } from './agentHubMap'
import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'
import { getCruiseMargin, mapCruiseDepartureToAgentHubDateRangePayload, mapTourVariationToAgentHubDateRangePayload } from 'lib/agentHub/agentHubUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getDateChangeJourneyV2IdKey, getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { FlightViewTypes } from 'constants/flight'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { matchBedbankRateOrItem } from 'lib/hotels/bedbankUtils'

type ServerAgentCommission = actions['calculateCartCommission']['responses']['200']['content']['application/json']['result'] & {
  calculatedIncentive?: number
  calculatedIncentiveAmount?: number
  totalCalculatedCommission?: number
  totalCalculatedDiscount?: number
  itemsCommission: Record<string, actions['calculateCartCommission']['responses']['200']['content']['application/json']['result']['itemsCommission'][string] & {
    incentivePercentage?: number
    incentiveMaxAmount?: number
    display?: App.AgentHubCommissionDisplay
  }>
}
type ServerOfferCommission = actions['calculateOffersCommission']['responses']['200']['content']['application/json']['result'][number] & {
  display?: App.AgentHubCommissionDisplay
}

type CommissionFlightCityCountry = {
  city?: string
  country?: string
}

type CommissionFlightEndpoints = {
  departure: CommissionFlightCityCountry
  arrival: CommissionFlightCityCountry
}

type CommissionAdditionalInfo = {
  selectedCabinClass?: App.Checkout.FlightItem['selectedCabinClass']
  marketingAirlineCode?: string
  bundledWithTypes?: Array<'flight'>
  type?: App.Checkout.TourV2ExperienceItemType
  optionalExperienceId?: string
  departure?: CommissionFlightCityCountry
  arrival?: CommissionFlightCityCountry
}

type CommissionDateRange = {
  startDate?: string
  endDate?: string
}

type CommissionLocation = {
  latitude: number
  longitude: number
}

type CommissionLocationSource = {
  latitude?: number | string | null
  longitude?: number | string | null
}

export type CalculateCartCommissionBodyItem = {
  offerId: string
  type: string
  location?: CommissionLocation
  price: number
  vendor: string
  provider?: string
  transactionKey: string
  mx?: string
  mrg?: number
  bundledOffers?: Array<string>
  additionalInfo?: CommissionAdditionalInfo
  startDate?: string
  endDate?: string
  taxesAndFees?: number
  experienceTicket?: {
    productId: string
  }
}

type CalculateCartCommissionBody = {
  cartId: string
  items: Array<CalculateCartCommissionBodyItem>
  total: number
  transactionKey: string
}

function bundledWithFlightAdditionalInfo(): NonNullable<CalculateCartCommissionBodyItem['additionalInfo']> {
  return { bundledWithTypes: ['flight'] }
}

function getFlightMarketingAirlineCodeForCommission(journey: App.JourneyV2): string | undefined {
  return journey.flightGroup?.flights?.[0]?.marketingAirline?.code
}

function isJourneyV2InFlightsStore(journey: App.AnyJourney): journey is App.JourneyV2 {
  return journey.itemType === 'journeyV2' && 'flightGroup' in journey
}

function hasCommissionFlightEndpointsData(endpoints: CommissionFlightEndpoints | undefined): boolean {
  if (!endpoints) return false
  return Boolean(
    endpoints.departure.city
    || endpoints.departure.country
    || endpoints.arrival.city
    || endpoints.arrival.country,
  )
}

function commissionFlightEndpointsFromJourneyV2(journey: App.JourneyV2 | undefined): CommissionFlightEndpoints | undefined {
  const firstLast = firstAndLast(journey?.flightGroup?.flights)
  if (!firstLast) return undefined
  const { first, last } = firstLast
  return {
    departure: { city: first.departureCity, country: first.departureCountry },
    arrival: { city: last.arrivalCity, country: last.arrivalCountry },
  }
}

function findReturnStartIndex(segments: Array<App.FlightV2>): number | undefined {
  if (segments.length < 2) return undefined
  const visitedArrivals = new Set<string>([segments[0].departureAirport])
  for (let i = 0; i < segments.length; i++) {
    const arr = segments[i].arrivalAirport
    if (visitedArrivals.has(arr) && i > 0) {
      return i
    }
    visitedArrivals.add(arr)
  }
  return undefined
}

/**
 * Outbound destination for a closed round-trip encoded as one journey.
 * Finds the first segment whose arrival airport was already reached earlier in the itinerary (repeat visit).
 * The turnaround city is the arrival of the previous segment (end of the “new” outbound portion).
 * Does not model unusual hub-out-and-back-then-continue outbound patterns (same hub revisited mid-outbound).
 */
function turnaroundCityCountryFromRoundTripSegments(segments: Array<App.FlightV2>): CommissionFlightCityCountry | undefined {
  const returnStartIndex = findReturnStartIndex(segments)
  if (returnStartIndex === undefined) return undefined
  const outboundLast = segments[returnStartIndex - 1]
  return {
    city: outboundLast.arrivalCity,
    country: outboundLast.arrivalCountry,
  }
}

function returnStartSegmentFromRoundTripSegments(segments: Array<App.FlightV2>): App.FlightV2 | undefined {
  const returnStartIndex = findReturnStartIndex(segments)
  return returnStartIndex === undefined
    ? undefined
    : segments[returnStartIndex]
}

function commissionFlightEndpointsFromJourneyV2GroupedOffer(journey: App.JourneyV2 | undefined): CommissionFlightEndpoints | undefined {
  const segments = journey?.flightGroup?.flights
  const firstLast = firstAndLast(segments)
  if (!firstLast) return undefined
  const { first, last } = firstLast
  if (segments && segments.length >= 2 && last.arrivalAirport === first.departureAirport) {
    const turnaround = turnaroundCityCountryFromRoundTripSegments(segments)
    if (turnaround && (turnaround.city || turnaround.country)) {
      return {
        departure: { city: first.departureCity, country: first.departureCountry },
        arrival: turnaround,
      }
    }
  }
  return commissionFlightEndpointsFromJourneyV2(journey)
}

function flightCommissionDateRange(startDate: string | undefined, endDate: string | undefined): CommissionDateRange {
  if (!startDate) return {}
  return {
    startDate,
    endDate: endDate ?? startDate,
  }
}

function commissionFlightDateRangeFromJourneyV2(journey: App.JourneyV2 | undefined): CommissionDateRange {
  const segments = journey?.flightGroup?.flights
  const firstFlight = segments?.[0]
  if (!firstFlight) return {}

  const endFlight = journey?.fareType === FlightViewTypes.RETURN ?
    returnStartSegmentFromRoundTripSegments(segments) ?? segments.at(-1) :
    undefined

  return flightCommissionDateRange(firstFlight.departingDate, endFlight?.departingDate)
}

function commissionFlightDateRangeFromJourneyFlight(journeyFlight: App.JourneyFlight | undefined): CommissionDateRange {
  const firstFlight = journeyFlight?.flights[0]
  return flightCommissionDateRange(firstFlight?.departingDate, undefined)
}

function commissionFlightEndpointsFromJourneyFlight(journeyFlight: App.JourneyFlight | undefined): CommissionFlightEndpoints | undefined {
  const firstLast = firstAndLast(journeyFlight?.flights)
  if (!firstLast) return undefined
  const { first, last } = firstLast
  return {
    departure: {
      city: first.departingDisplayNames?.primary ?? first.departingAirportName,
      country: first.departingCountry,
    },
    arrival: {
      city: last.arrivalDisplayNames?.primary ?? last.arrivalAirportName,
      country: last.arrivalCountry,
    },
  }
}

function commissionFlightEndpointsFromAirportCodesForLeg(
  leg: App.Checkout.FlightItemFlight,
  airportsByCode: Map<string, App.Airport>,
): CommissionFlightEndpoints {
  const departureAirport = leg.departingAirportCode ? airportsByCode.get(leg.departingAirportCode) : undefined
  const arrivalAirport = leg.arrivalAirportCode ? airportsByCode.get(leg.arrivalAirportCode) : undefined
  return {
    departure: {
      city: departureAirport?.name ?? leg.departingAirportName ?? leg.departingAirportCode ?? '',
      country: countryNameFromAirportCode(departureAirport?.countryCode),
    },
    arrival: {
      city: arrivalAirport?.name ?? leg.arrivalAirportName ?? leg.arrivalAirportCode ?? '',
      country: countryNameFromAirportCode(arrivalAirport?.countryCode),
    },
  }
}

/** Grouped line: departure from home/origin; arrival = outward destination (first leg arrival), not the final return arrival. */
function commissionFlightEndpointsFromGroupedAirportFallback(
  item: App.Checkout.FlightItem,
  firstLeg: App.Checkout.FlightItemFlight,
  airportsByCode: Map<string, App.Airport>,
): CommissionFlightEndpoints {
  const departureAirport = item.originAirportCode ? airportsByCode.get(item.originAirportCode) : undefined
  const turnaroundCode = firstLeg.arrivalAirportCode
  const turnaroundAirport = turnaroundCode ? airportsByCode.get(turnaroundCode) : undefined
  return {
    departure: {
      city: departureAirport?.name ?? firstLeg.departingAirportName ?? item.originAirportCode ?? '',
      country: countryNameFromAirportCode(departureAirport?.countryCode),
    },
    arrival: {
      city: turnaroundAirport?.name ?? firstLeg.arrivalAirportName ?? turnaroundCode ?? '',
      country: countryNameFromAirportCode(turnaroundAirport?.countryCode),
    },
  }
}

function cartLegJourneyStoreKey(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): string | undefined {
  if (!item.searchId) return undefined
  return postPurchaseMode === 'flight-change-dates'
    ? item.reservationId
      ? getDateChangeJourneyV2IdKey(item.reservationId, item.searchId, leg.journeyId)
      : undefined
    : getJourneyV2IdKey(leg.journeyId, item.searchId, leg.fareFamily?.id)
}

function getJourneyV2ForCartLeg(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): App.JourneyV2 | undefined {
  const key = cartLegJourneyStoreKey(item, leg, postPurchaseMode)
  if (!key) return undefined
  const stored = journeysById[key]
  if (!stored || !isJourneyV2InFlightsStore(stored)) return undefined
  return stored
}

function countryNameFromAirportCode(countryCode: string | undefined): string {
  if (!countryCode) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

type CartFlightLegCommissionContext = {
  item: App.Checkout.FlightItem
  leg: App.Checkout.FlightItemFlight
  legIndex: number
  flightItemView: App.Checkout.FlightItemView
  airportsByCode: Map<string, App.Airport>
  journeysById: App.State['flights']['journeysById']
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>
}

function cartFlightCommissionEndpointsForCartLeg(
  {
    item,
    leg,
    legIndex,
    flightItemView,
    airportsByCode,
    journeysById,
    postPurchaseMode,
  }: CartFlightLegCommissionContext,
): CommissionFlightEndpoints | undefined {
  const fromStore = commissionFlightEndpointsFromJourneyV2(
    getJourneyV2ForCartLeg(item, leg, journeysById, postPurchaseMode),
  )
  if (fromStore) return fromStore
  const viewFlight = flightItemView.flights[legIndex]
  const fromView = commissionFlightEndpointsFromJourneyFlight(viewFlight?.journeyFlight)
  if (fromView) return fromView
  return commissionFlightEndpointsFromAirportCodesForLeg(leg, airportsByCode)
}

function cartFlightCommissionEndpointsForGroupedFlightItem(
  flightItemView: App.Checkout.FlightItemView,
  airportsByCode: Map<string, App.Airport>,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): CommissionFlightEndpoints | undefined {
  const seenKeys = new Set<string>()
  for (const leg of flightItemView.item.flights) {
    const key = cartLegJourneyStoreKey(flightItemView.item, leg, postPurchaseMode)
    if (!key || seenKeys.has(key)) continue
    const stored = journeysById[key]
    if (!stored || !isJourneyV2InFlightsStore(stored)) continue
    seenKeys.add(key)
    const outboundEndpoints = commissionFlightEndpointsFromJourneyV2GroupedOffer(stored)
    if (outboundEndpoints) return outboundEndpoints
  }

  const views = flightItemView.flights
  if (views.length > 0) {
    const outboundEndpoints = commissionFlightEndpointsFromJourneyFlight(views[0]?.journeyFlight)
    if (outboundEndpoints) return outboundEndpoints
  }

  const item = flightItemView.item
  const firstLeg = item.flights[0]
  if (!firstLeg) return undefined
  return commissionFlightEndpointsFromGroupedAirportFallback(item, firstLeg, airportsByCode)
}

function cartFlightCommissionDateRangeForGroupedFlightItem(
  flightItemView: App.Checkout.FlightItemView,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): CommissionDateRange {
  const item = flightItemView.item
  const firstLeg = item.flights[0]
  const lastLeg = item.flights.at(-1)

  if (item.flights.length > 1) {
    return flightCommissionDateRange(
      firstLeg?.departingDate ?? item.travelStart,
      lastLeg?.departingDate ?? item.travelEnd,
    )
  }

  const firstLegJourney = firstLeg ? getJourneyV2ForCartLeg(item, firstLeg, journeysById, postPurchaseMode) : undefined
  const fromStore = commissionFlightDateRangeFromJourneyV2(firstLegJourney)
  if (fromStore.startDate) return fromStore

  const fromView = commissionFlightDateRangeFromJourneyFlight(flightItemView.flights[0]?.journeyFlight)
  if (fromView.startDate) return fromView

  return flightCommissionDateRange(firstLeg?.departingDate ?? item.travelStart, item.travelEnd)
}

function marketingAirlineCodeForCartLeg(
  item: App.Checkout.FlightItem,
  leg: App.Checkout.FlightItemFlight,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): string | undefined {
  const journey = getJourneyV2ForCartLeg(item, leg, journeysById, postPurchaseMode)
  return journey ? getFlightMarketingAirlineCodeForCommission(journey) : undefined
}

function flightCartAdditionalInfo(
  selectedCabinClass: App.Checkout.FlightItem['selectedCabinClass'],
  marketingAirlineCode: string | undefined,
  endpoints?: CommissionFlightEndpoints,
): CommissionAdditionalInfo | undefined {
  const hasEndpointData = hasCommissionFlightEndpointsData(endpoints)
  if (!selectedCabinClass && !marketingAirlineCode && !hasEndpointData) return undefined
  return {
    ...(selectedCabinClass && { selectedCabinClass }),
    ...(marketingAirlineCode && { marketingAirlineCode }),
    ...(hasEndpointData && endpoints && {
      departure: endpoints.departure,
      arrival: endpoints.arrival,
    }),
  }
}

function offerFlightCommissionAdditionalInfo(
  marketingAirlineCode: string | undefined,
  endpoints: CommissionFlightEndpoints | undefined,
): CommissionAdditionalInfo | undefined {
  return flightCartAdditionalInfo(undefined, marketingAirlineCode, endpoints)
}

const NEW_TOUR_PRODUCT_TYPES = [PRODUCT_TYPE_TOUR_DELUXE, PRODUCT_TYPE_TOUR_PREMIUM, PRODUCT_TYPE_TOUR_ULTRA_LUX, PRODUCT_TYPE_TOUR_SIGNATURE_SERIES, PRODUCT_TYPE_TOUR_CUSTOM]

function mapCommissionLocation(source: CommissionLocationSource | undefined | null): CommissionLocation | undefined {
  if (source?.latitude === undefined || source.latitude === null || source.longitude === undefined || source.longitude === null) return undefined

  const latitude = Number(source.latitude)
  const longitude = Number(source.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined

  return { latitude, longitude }
}

function commissionLocationPayload(source: CommissionLocationSource | undefined | null): { location: CommissionLocation } | {} {
  const location = mapCommissionLocation(source)
  return location ? { location } : {}
}

function getTourV2VariationStartLocation(variation: App.Tours.TourV2OfferVariation | undefined): CommissionLocation | undefined {
  const firstItineraryLocation = min(variation?.itinerary ?? [], itineraryItem => itineraryItem.startDay)
    ?.locationsVisitedDetails
    .find(location => mapCommissionLocation(location))

  return mapCommissionLocation(firstItineraryLocation)
}

const getTourV2StartLocation = (item: App.Checkout.TourV2AccommodationOfferView): CommissionLocation | undefined => {
  return getTourV2VariationStartLocation(item.itemViews[0]?.variation)
}

function getCruiseStartLocation(offer: App.CruiseOffer | undefined): CommissionLocation | undefined {
  const firstItineraryPort = min(offer?.itinerary ?? [], itineraryItem => itineraryItem.startDay)
    ?.port

  return mapCommissionLocation(firstItineraryPort)
}

const sumViewItemTotal = (itemView: App.Checkout.AnyItemView): number => {
  const itemPrice = itemView.totals.price
  const isTourV2 = itemView.item.itemType === 'tourV2'
  const itemTotalPrice = isTourV2 ? itemView.item?.total ?? itemPrice : itemPrice
  const fullPrice = itemTotalPrice + (itemView.totals.surcharge ?? 0) + (itemView.totals.otherFees?.extraGuestSurcharge ?? 0)
  const fullPriceSubtractedPaidPrice = fullPrice - (itemView.totals.paidPrice ?? 0)
  return isTourV2 ? Math.max(fullPriceSubtractedPaidPrice, 0) : fullPrice
}
const sumViewItemsTotal = (items: Array<App.Checkout.AnyItemView>): number => {
  return sum(items, (i) => sumViewItemTotal(i))
}

const processExperienceItems = (experienceItemsView: Array<App.Checkout.ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  for (const item of experienceItemsView) {
    for (const ticket of item.ticketViews) {
      items.push({
        offerId: item.experienceId,
        type: OFFER_TYPE_EXPERIENCE,
        price: ticket.item.total ?? item.totals.price / (item.ticketViews.length ?? 1),
        ...commissionLocationPayload(item),
        vendor: item.vendor,
        provider: item.provider,
        transactionKey: ticket.item.transactionKey,
        startDate: item.bookingDate,
        endDate: item.bookingDate,
        experienceTicket: {
          productId: ticket.ticket.productId,
        },
        ...(item.experience.mrg !== undefined && { mrg: item.experience.mrg }),
      })
    }
  }

  return items
}

const processTransferItems = (transferItemsView: Array<App.Checkout.TransferItemView> = []): Array<CalculateCartCommissionBodyItem> => {
  return transferItemsView.map((itemView) => ({
    offerId: itemView.item.experienceId,
    type: OFFER_TYPE_EXPERIENCE_TRANSFER,
    price: itemView.item.transfer.option?.price ?? 0,
    ...commissionLocationPayload(itemView.item.transfer.hotel),
    vendor: itemView.vendor,
    provider: itemView.provider,
    transactionKey: itemView.item.transactionKey,
    startDate: itemView.item.transfer.date,
    endDate: itemView.item.transfer.date,
  }))
}

const processCarHireItems = (carHireItemsView: Array<App.Checkout.CarHireItemView>): Array<CalculateCartCommissionBodyItem> => {
  return (carHireItemsView ?? []).map((itemView) => ({
    offerId: itemView.item.offerId,
    type: PRODUCT_TYPE_CAR_HIRE,
    price: itemView.item.price,
    ...commissionLocationPayload(itemView.pickUpLocation),
    vendor: '',
    transactionKey: itemView.item.transactionKey,
  }))
}

function findBedbankRateForCartCommission(
  offerRates: App.State['offer']['bedbankOfferRates'],
  item: App.Checkout.BedbankHotelItem,
  groupedItems: Array<App.Checkout.BedbankHotelItem>,
): App.BedbankRate | undefined {
  const combinedOccupancy = groupedItems.filter(groupItem => groupItem.sessionId === item.sessionId).map(groupItem => groupItem.occupancy)
  const combinedRateKey = getBedbankRateKey(combinedOccupancy.length ? combinedOccupancy : [item.occupancy], item.checkIn, item.checkOut)
  const perRoomRateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
  const rates = offerRates[item.offerId]?.[combinedRateKey] ?? offerRates[item.offerId]?.[perRoomRateKey]

  return rates?.find(rate => rate.id === item.roomRateId && rate.isFlightBundle === item.isFlightBundle) ??
    rates?.find(rate => matchBedbankRateOrItem(item, rate))
}

const processBedBankItems = (
  item: App.Checkout.BedbankAccommodationOfferView,
  bedbankOfferRates: App.State['offer']['bedbankOfferRates'],
): Array<CalculateCartCommissionBodyItem> => {
  return item.itemViews.map((itemView) => {
    const rateMx = itemView.rate?.mx ?? findBedbankRateForCartCommission(bedbankOfferRates, itemView.item, item.itemViews.map(view => view.item))?.mx

    return {
      type: item.offer?.productType ?? '',
      offerId: item.offerId,
      price: sumViewItemTotal(itemView),
      ...commissionLocationPayload(item.offer?.property),
      vendor: '',
      transactionKey: itemView.item.transactionKey,
      mx: rateMx,
      startDate: itemView.item.checkIn,
      endDate: itemView.item.checkOut,
      ...(itemView.item.isFlightBundle && {
        additionalInfo: bundledWithFlightAdditionalInfo(),
      }),
    }
  })
}

const processTourV2Items = (item: App.Checkout.TourV2AccommodationOfferView, allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []

  const currentTourOptionalExperiences = allOptionalExperiences.filter((oe) => oe.item.offerId === item.offerId)
  const leahProductType = NEW_TOUR_PRODUCT_TYPES.includes(item.offer?.productType ?? '') ?
    DIRECT_TOUR :
    item.offer?.productType ?? ''
  const location = getTourV2StartLocation(item)

  items.push({
    type: leahProductType,
    offerId: item.offerId,
    price: sumViewItemsTotal(item.itemViews),
    ...(location && { location }),
    vendor: '',
    transactionKey: item.itemViews[0].item.transactionKey,
    startDate: item.itemViews[0].item.startDate,
    endDate: item.itemViews[0].item.endDate,
  })

  for (const optionalExperience of currentTourOptionalExperiences) {
    items.push(...processTourV2OptionalExperienceItems([optionalExperience]))
  }

  return items
}

const processCruiseItems = (item: App.Checkout.CruiseAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  const location = getCruiseStartLocation(item.offer)

  return item.itemViews.map((itemView) => ({
    type: item.offer?.productType ?? '',
    offerId: item.offerId,
    price: sumViewItemTotal(itemView),
    ...(location && { location }),
    mrg: getCruiseMargin(itemView),
    vendor: item.offer?.cruiseLine?.code ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.item.startDate && { startDate: itemView.item.startDate }),
    ...(itemView.item.endDate && { endDate: itemView.item.endDate }),
    taxesAndFees: itemView.totals.taxesAndFees ?? 0,
  }))
}

const processAlwaysOnItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    ...commissionLocationPayload(item.offer?.property),
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.roomRate?.mx && { mx: itemView.pkg?.roomRate?.mx }),
    startDate: item.startDate,
    endDate: item.endDate,
    ...(item.offer?.offerFlightsEnabled && { additionalInfo: bundledWithFlightAdditionalInfo() }),
  }))
}

const processFlightItems = (
  flightItemsView: Array<App.Checkout.FlightItemView>,
  accommodationItems: Array<App.Checkout.AccommodationOfferView>,
  airports: Array<App.Airport>,
  journeysById: App.State['flights']['journeysById'],
  postPurchaseMode: ReturnType<typeof findPostPurchaseCheckout>,
): Array<CalculateCartCommissionBodyItem> => {
  const items: Array<CalculateCartCommissionBodyItem> = []
  const airportsByCode = arrayToMap(airports, airport => airport.code)

  for (const flightItemView of flightItemsView) {
    const isTwoOneWays = flightItemView.viewType === 'TwoOneWays'
    const hasMultipleLegs = flightItemView.item.flights.length > 1
    const flightAddons = flightItemView.item.otherFees || {}
    const addonTotalPrice = sum(Object.values(flightAddons), (fee) => fee as number)
    const flightDates = cartFlightCommissionDateRangeForGroupedFlightItem(
      flightItemView,
      journeysById,
      postPurchaseMode,
    )

    const bundledOffers = accommodationItems?.flatMap(accommodation => accommodation.itemViews.filter(accommodationItemView => flightItemView.item.bundledItemIds?.includes(accommodationItemView.item.itemId))).map(offer => offer.item.offerId).filter(id => !!id)

    if (isTwoOneWays && hasMultipleLegs) {
      const allLegsAreDomesticAU = flightItemView.flights.every((fv) => {
        const leg0 = fv.journeyFlight?.flights?.[0]
        return leg0?.departingCountry === 'Australia' && leg0?.arrivalCountry === 'Australia'
      })
      const providers = flightItemView.item.flights.map(f => f.provider).filter(Boolean)
      const hasDifferentProviders = new Set(providers).size > 1

      const splitIntoPerLegCommissionLines =
        !allLegsAreDomesticAU || hasDifferentProviders

      if (splitIntoPerLegCommissionLines) {
        flightItemView.item.flights.forEach((flight, index) => {
          const marketingAirlineCode = marketingAirlineCodeForCartLeg(flightItemView.item, flight, journeysById, postPurchaseMode)
          const legContext: CartFlightLegCommissionContext = {
            item: flightItemView.item,
            leg: flight,
            legIndex: index,
            flightItemView,
            airportsByCode,
            journeysById,
            postPurchaseMode,
          }
          const legEndpoints = cartFlightCommissionEndpointsForCartLeg(
            legContext,
          )
          const additionalInfo = flightCartAdditionalInfo(
            flightItemView.item.selectedCabinClass,
            marketingAirlineCode,
            legEndpoints,
          )
          const bookingLevelAddonForLeg = index === 0 ? addonTotalPrice : 0
          items.push({
            offerId: flight.journeyId,
            transactionKey: flightItemView.item.transactionKey,
            price: (flight.cost ?? 0) + bookingLevelAddonForLeg,
            type: CHECKOUT_ITEM_TYPE_FLIGHT,
            vendor: flight.provider,
            ...flightDates,
            ...(bundledOffers.length > 0 && { bundledOffers }),
            ...(additionalInfo && { additionalInfo }),
          })
        })
        continue
      }
    }

    const firstLeg = flightItemView.item.flights[0]
    const defaultMarketingAirlineCode = firstLeg ? marketingAirlineCodeForCartLeg(flightItemView.item, firstLeg, journeysById, postPurchaseMode) : undefined
    const groupedEndpoints = cartFlightCommissionEndpointsForGroupedFlightItem(
      flightItemView,
      airportsByCode,
      journeysById,
      postPurchaseMode,
    )
    const defaultAdditionalInfo = flightCartAdditionalInfo(
      flightItemView.item.selectedCabinClass,
      defaultMarketingAirlineCode,
      groupedEndpoints,
    )
    items.push({
      offerId: flightItemView.item.flights?.[0]?.journeyId ?? '',
      transactionKey: flightItemView.item.transactionKey,
      price: flightItemView.totals.price + addonTotalPrice,
      type: CHECKOUT_ITEM_TYPE_FLIGHT,
      vendor: flightItemView.provider ?? flightItemView.item.flights[0]?.provider ?? '',
      ...flightDates,
      ...(bundledOffers.length > 0 && { bundledOffers }),
      ...(defaultAdditionalInfo && { additionalInfo: defaultAdditionalInfo }),
    })
  }

  return items
}

const processDefaultItems = (item: App.Checkout.LEAccommodationOfferView): Array<CalculateCartCommissionBodyItem> => {
  return item.itemViews.map((itemView) => ({
    offerId: item.offerId,
    type: item.offer?.productType ?? item.offerType,
    price: sumViewItemTotal(itemView),
    ...commissionLocationPayload(item.offer?.property),
    vendor: item.offer?.vendorName ?? '',
    transactionKey: itemView.item.transactionKey,
    ...(itemView.pkg?.mx && { mx: itemView.pkg?.mx }),
    startDate: item.startDate,
    endDate: item.endDate,
    ...(item.offer?.offerFlightsEnabled && { additionalInfo: bundledWithFlightAdditionalInfo() }),
  }))
}

const processVillaItems = (villaViews: Array<App.Checkout.VillaItemView>): Array<CalculateCartCommissionBodyItem> => {
  return villaViews.map((view) => {
    return {
      offerId: view.item.offerId,
      type: view.offer?.productType ?? 'rental',
      price: sumViewItemTotal(view),
      ...commissionLocationPayload(view.offer?.property),
      vendor: view.offer?.vendorName ?? '',
      transactionKey: view.item.transactionKey,
      ...(view.package?.mx && { mx: view.package.mx }),
      startDate: view.item.checkIn,
      endDate: view.item.checkOut,
      ...(view.offer?.offerFlightsEnabled && { additionalInfo: bundledWithFlightAdditionalInfo() }),
    }
  })
}

const processAccommodationItem = (
  item: App.Checkout.AccommodationOfferView,
  allOptionalExperiences: Array<App.Checkout.TourV2ExperienceItemView>,
  bedbankOfferRates: App.State['offer']['bedbankOfferRates'],
): Array<CalculateCartCommissionBodyItem> => {
  switch (item.offerType) {
    case OFFER_TYPE_TOUR:
      return processDefaultItems(item)
    case OFFER_TYPE_BED_BANK:
      return processBedBankItems(item, bedbankOfferRates)
    case OFFER_TYPE_TOUR_V2:
      return processTourV2Items(item, allOptionalExperiences)
    case OFFER_TYPE_CRUISE:
      return processCruiseItems(item)
    case OFFER_TYPE_ALWAYS_ON:
      return processAlwaysOnItems(item)
    default:
      return processDefaultItems(item)
  }
}

const processTourV2OptionalExperienceItems = (data: Array<App.Checkout.TourV2ExperienceItemView>): Array<CalculateCartCommissionBodyItem> => {
  return data.map((item) => ({
    type: DIRECT_TOUR,
    // must be the id of the tour so that it applies the same commission as the tour
    offerId: item.item.purchasableOption.fkTourId,
    price: item.item.total ?? item.item.purchasableOption.fullPrice,
    vendor: '',
    startDate: item.item.date,
    endDate: item.item.date,
    transactionKey: item.item.transactionKey,
    additionalInfo: {
      type: item.item.itemType,
      optionalExperienceId: item.item.offerId,
    },
  }))
}

const getCommissionItems = (state: App.State): Array<CalculateCartCommissionBodyItem> => {
  const { accommodationItemsView, flightItemsView, tourV2ExperienceItemsView, experienceItemsView, transferItemsView, carHireItemsView, villaItemsView } =
    getAllItemViewsByType(state).data
  const allOptionalExperiences = tourV2ExperienceItemsView?.data ?? []

  const experienceItems = processExperienceItems(experienceItemsView.data)
  const transferItems = processTransferItems(transferItemsView.data)
  const carHireItems = processCarHireItems(carHireItemsView.data)
  const flightItems = processFlightItems(
    flightItemsView.data,
    accommodationItemsView.data,
    state.geo.airports,
    state.flights.journeysById,
    findPostPurchaseCheckout(state.checkout.cart.mode),
  )
  const accommodationItems = accommodationItemsView.data.flatMap((item) => processAccommodationItem(item, allOptionalExperiences, state.offer.bedbankOfferRates))
  const villaItems = processVillaItems(villaItemsView.data)

  // when checkout is type "tour-optional-extras", it allows the user to have an optional experience without the tour item in the checkout
  // so we must process them outside of the "tourV2" scope
  const isOptionalExperiencesCheckout = state.checkout.cart.mode === 'tour-optional-experiences'
  const tourOptionalExperiences = isOptionalExperiencesCheckout ? processTourV2OptionalExperienceItems(allOptionalExperiences) : []

  return [...experienceItems, ...transferItems, ...carHireItems, ...flightItems, ...accommodationItems, ...villaItems, ...tourOptionalExperiences]
}

export const stateToCommissionOrder = (state: App.State): CalculateCartCommissionBody => {
  const items = getCommissionItems(state)
  return {
    items,
    cartId: state.checkout.cart.cartId,
    total: sum(items, (i) => i.price ?? 0),
    transactionKey: state.checkout.cart.transactionKey,
  }
}

export const agentHubCommissionMap = (serverCommission: ServerAgentCommission): App.AgentHubCommission => {
  return {
    promoCode: serverCommission.promoCode,
    calculatedCommission: serverCommission.calculatedCommission,
    calculatedDiscount: serverCommission.calculatedDiscount,
    calculatedIncentive: serverCommission.calculatedIncentive,
    calculatedIncentiveAmount: serverCommission.calculatedIncentiveAmount,
    totalCalculatedCommission: serverCommission.totalCalculatedCommission,
    totalCalculatedDiscount: serverCommission.totalCalculatedDiscount,
    itemsCommission: Object.fromEntries(
      Object.entries(serverCommission.itemsCommission).map(([key, value]) => {
        return [
          key,
          {
            calculatedCommissionPercentage: value.calculatedCommissionPercentage ?? value.commissionPercentage,
            commissionPercentage: value.commissionPercentage,
            itemDiscount: value.itemDiscount,
            itemPrice: value.itemPrice,
            incentivePercentage: value.incentivePercentage,
            incentiveMaxAmount: value.incentiveMaxAmount,
            includesTaxesAndFees: value.includesTaxesAndFees ?? true,
            includesMargin: value.includesMargin ?? true,
            ...(value.display && { display: value.display }),
          },
        ]
      }),
    ),
  }
}

export const mapOfferCommission = (
  serverOfferCommission: ServerOfferCommission & {
    dateRanges: Array<{ id: string; startDate: string; endDate: string }>
    includesTaxesAndFees?: boolean
    campaigns?: Array<string>
  },
): App.AgentHubOfferCommission => {
  return {
    offerId: serverOfferCommission.offerId,
    commissionPercentage: serverOfferCommission.commission,
    baseCommissionPercentage: serverOfferCommission.baseCommission,
    additionalCommissionsPercentage: serverOfferCommission.additionalCommissions,
    packages: serverOfferCommission.packages.map((pkg) => ({
      packageId: pkg.packageId,
      commissionPercentage: pkg.commission,
      baseCommissionPercentage: pkg.baseCommission,
      additionalCommissionsPercentage: pkg.additionalCommissions,
    })),
    minBaseCommission: serverOfferCommission.minBaseCommission,
    maxBaseCommission: serverOfferCommission.maxBaseCommission,
    dateRangesCommission: arrayToObject(serverOfferCommission.dateRanges, (dateRange) => dateRange.id),
    includesTaxesAndFees: serverOfferCommission.includesTaxesAndFees ?? true,
    incentivePercentage: serverOfferCommission.incentivePercentage,
    incentiveMaxAmount: serverOfferCommission.incentiveMaxAmount,
    incentiveAmount: serverOfferCommission.incentiveAmount,
    includesMargin: serverOfferCommission.includesMargin ?? true,
    campaigns: serverOfferCommission.campaigns ?? [],
    ...(serverOfferCommission.display && { display: serverOfferCommission.display }),
  }
}

export const mapOfferToAgentHubCommissionPayload = (offer: App.AgentHubOfferPayload, { offerRates = {}, bundledOffers = [] }: { offerRates?: Record<string, Array<App.BedbankRate>>, bundledOffers?: Array<string> } = {}): AgentHubGetOffersCommissionBody => {
  const mappedData: AgentHubGetOffersCommissionBody = match(offer)
    .when((data) => 'journey' in data, (flightWithJourney) => {
      const offerEndpoints = commissionFlightEndpointsFromJourneyV2GroupedOffer(flightWithJourney.journey)
      const flightAdditionalInfo = offerFlightCommissionAdditionalInfo(
        getFlightMarketingAirlineCodeForCommission(flightWithJourney.journey),
        offerEndpoints,
      )
      const flightDates = commissionFlightDateRangeFromJourneyV2(flightWithJourney.journey)
      return {
        offerId: flightWithJourney.journey.id,
        type: 'flight' as const,
        vendor: flightWithJourney.journey.provider,
        price: flightWithJourney.journey.price.all.totalRoundTripPrice,
        packages: [],
        ...flightDates,
        ...(bundledOffers.length > 0 && { bundledOffers }),
        ...(flightAdditionalInfo && { additionalInfo: flightAdditionalInfo }),
      }
    })
    .when(isCarHireOffer, (carOffer) => ({
      offerId: carOffer.id,
      type: carOffer.productType,
      vendor: carOffer.vendor.name,
      price: carOffer.price,
      ...commissionLocationPayload(carOffer.pickUp),
      packages: [],
    }))
    .when(isBedbankOffer, (bedbankOffer) => {
      // packages are supposed to exist at this point.
      // this `packages?.[0]?` is just a safeguard because of an error happening when the user agent is Google Adbots
      const price = bedbankOffer.packages?.[0]?.sell?.value ?? 0
      return {
        type: bedbankOffer.productType,
        offerId: bedbankOffer.id,
        vendor: '',
        price,
        ...commissionLocationPayload(bedbankOffer.property),
        packages: Object.keys(offerRates).length > 0 ? offerRates[Object.keys(offerRates)[0]].map(mapBedbankPackageToAgentHubPayload) : [],
      }
    })
    .when(isTourV2Offer, (tourOffer) => {
      const datePayload = Object.values(tourOffer.variations).flatMap(mapTourVariationToAgentHubDateRangePayload)
      const location = getTourV2VariationStartLocation(Object.values(tourOffer.variations)[0])
      return {
        offerId: tourOffer.id,
        type: NEW_TOUR_PRODUCT_TYPES.includes(tourOffer.productType) ? DIRECT_TOUR : tourOffer.productType,
        vendor: '',
        price: 0,
        ...(location && { location }),
        packages: [],
        dateRanges: datePayload,
      }
    })
    .when(isExperienceOffer, (expOffer) => ({
      offerId: expOffer.id,
      type: expOffer.productType !== OFFER_TYPE_EXPERIENCE_TRANSFER ? OFFER_TYPE_EXPERIENCE as App.OfferType : expOffer.productType,
      vendor: expOffer.vendor?.name ?? '',
      provider: expOffer.provider,
      price: expOffer.price,
      ...commissionLocationPayload(expOffer.location),
      packages: [],
      ...(expOffer.mrg !== undefined && { mrg: expOffer.mrg }),
    }))
    .when(isCruiseOffer, (cruiseOffer) => {
      const departures = cruiseOffer.departuresSummary || cruiseOffer.departures
      const departuresArray = Object.values(departures) as unknown as Array<typeof departures[string]>
      const location = getCruiseStartLocation(cruiseOffer)
      return {
        offerId: cruiseOffer.id,
        type: cruiseOffer.productType,
        vendor: cruiseOffer.cruiseLine?.code ?? '',
        price: cruiseOffer.mainDepartureDetails.lowestOverallPriceDetails.total,
        ...(location && { location }),
        dateRanges: departuresArray.flatMap(val => val.map(mapCruiseDepartureToAgentHubDateRangePayload)),
        packages: [],
      }
    })
    .otherwise((defaultOffer) => ({
      offerId: defaultOffer.id,
      type: defaultOffer.productType,
      vendor: defaultOffer.vendorName ?? '',
      price: defaultOffer.lowestPricePackage?.price ?? 0,
      ...commissionLocationPayload(defaultOffer.property),
      packages: getHotelPackages(defaultOffer),
      ...(defaultOffer.offerFlightsEnabled && {
        additionalInfo: bundledWithFlightAdditionalInfo(),
      }),
    }))

  return {
    offerId: mappedData.offerId,
    price: mappedData.price,
    type: mappedData.type,
    vendor: mappedData.vendor,
    ...(mappedData.provider !== undefined && { provider: mappedData.provider }),
    ...(mappedData.location && { location: mappedData.location }),
    packages: mappedData.packages,
    ...(mappedData.bundledOffers && { bundledOffers: mappedData.bundledOffers }),
    ...(mappedData.dateRanges && { dateRanges: mappedData.dateRanges }),
    ...(mappedData.additionalInfo && { additionalInfo: mappedData.additionalInfo }),
    ...(mappedData.startDate && { startDate: mappedData.startDate }),
    ...(mappedData.endDate && { endDate: mappedData.endDate }),
    ...(mappedData.mrg !== undefined && { mrg: mappedData.mrg }),
  }
}

const getHotelPackages = (offer: App.Offer | App.TourOfferSummary): AgentHubGetOffersCommissionBody['packages'] => {
  return match(offer)
    .when(isLpcHotelOffer, (lpcOffer) => {
      if (!lpcOffer?.packages) {
        return []
      }

      return lpcOffer.packages.filter(pkg => !!pkg.roomRate?.mx).map(mapLPCPackageToAgentHubPayload)
    })
    .when(isLEHotel, (hotelOffer) => {
      if (!hotelOffer?.packages) {
        return []
      }

      return hotelOffer.packages.filter(pkg => !!pkg.mx).map(mapFlashPackageToAgentHubPayload)
    })
    .otherwise(() => [])
}
